import { todayInput } from "../../shared/utils/date";

export type WorkbenchVoucherKind = "SalesInvoice" | "PurchaseInvoice";

export type GridMetaField = {
  name: string;
  label: string;
  required?: boolean;
  isDynamic?: boolean;
  defCode?: string;
};

export type BillLineRow = {
  key: string;
  ledgerName: string;
  sacCode?: string;
  quantity: number;
  rate: number;
  amount: number;
  per?: string;
  gstPercent: number;
  tdsPercent: number;
  remarks?: string;
  dynamicValues: Record<string, number>;
};

function normalizedAmount(line: BillLineRow) {
  const explicitAmount = Number(line.amount ?? 0);
  if (explicitAmount !== 0) return explicitAmount;
  return Number(line.quantity ?? 0) * Number(line.rate ?? 0);
}

export type PreviewInfo = {
  taxableAmount?: number;
  cgstTotal?: number;
  sgstTotal?: number;
  igstTotal?: number;
  tdsTotal?: number;
  totalAmount?: number;
  grandTotal?: number;
};

export const monthOptions = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
].map((value) => ({ value, label: value }));

export const unitOptions = ["NOS", "PCS", "DAY", "HOUR", "MONTH", "KG", "LTR"].map((value) => ({ value, label: value }));

export function newLine(): BillLineRow {
  return {
    key: crypto.randomUUID(),
    ledgerName: "",
    quantity: 0,
    rate: 0,
    amount: 0,
    per: "NOS",
    gstPercent: 0,
    tdsPercent: 0,
    dynamicValues: {}
  };
}

export function extractId(value: any) {
  return value?.id ?? value ?? null;
}

export function buildPayload({
  values,
  lines,
  voucherType,
  tempKey
}: {
  values: Record<string, any>;
  lines: BillLineRow[];
  voucherType: WorkbenchVoucherKind;
  tempKey: string;
}) {
  return {
    voucherType,
    billNo: values.billNo ?? "",
    billDate: values.billDate?.format?.("YYYY-MM-DD") ?? todayInput(),
    dueDate: values.dueDate?.format?.("YYYY-MM-DD"),
    partyId: extractId(values.partyId),
    ledgerAccountId: extractId(values.ledgerAccountId),
    sacCode: values.sacCode,
    referenceNo: values.referenceNo,
    narration: values.narration,
    adjustmentMethod: "NewRef",
    adjustmentEntries: [],
    lineItems: lines.map((line, index) => {
      const amount = normalizedAmount(line);
      const isDeduction = amount < 0;
      const quantity = isDeduction
        ? (Number(line.quantity ?? 0) > 0 ? Number(line.quantity ?? 0) : 1)
        : Number(line.quantity ?? 0);
      const rate = isDeduction
        ? (Number(line.quantity ?? 0) > 0
          ? Number((amount / Number(line.quantity ?? 0)).toFixed(6))
          : amount)
        : Number(line.rate ?? 0);

      return {
        rowNo: index + 1,
        ledgerName: line.ledgerName,
        sacCode: line.sacCode ?? values.sacCode ?? "",
        quantity,
        rate,
        per: isDeduction ? "NOS" : line.per,
        amount,
        // Preserve explicitly selected tax rates for deductions. The API applies
        // the signed amount so these rows reduce GST/TDS proportionately.
        gstPercent: line.gstPercent,
        tdsPercent: line.tdsPercent,
        remarks: isDeduction ? "Deduction" : line.remarks,
        inputs: Object.entries(line.dynamicValues ?? {}).map(([code, value]) => ({
          code,
          name: code,
          value,
          rowNo: index + 1
        }))
      };
    }),
    inputs: lines.flatMap((line, index) => Object.entries(line.dynamicValues ?? {}).map(([code, value]) => ({
      code,
      name: code,
      value,
      rowNo: index + 1
    }))),
    tempKey
  };
}
